import React from 'react';
import detailStyles from '../components/forms/DetailsModal.module.css';

/**
 * Shared helpers for per-company custom fields on leads and projects.
 *
 * Both the Leads and Projects pages build their columns, cells, sorting and
 * detail-modal rows from these, so the behaviour stays identical across the two
 * without either page having to know how a field type works.
 */

export const CUSTOM_FIELD_TYPES = [
  { value: 'text', label: 'Text', hasOptions: false },
  { value: 'textarea', label: 'Long text', hasOptions: false },
  { value: 'number', label: 'Number', hasOptions: false },
  { value: 'currency', label: 'Currency', hasOptions: false },
  { value: 'date', label: 'Date', hasOptions: false },
  { value: 'select', label: 'Single select', hasOptions: true },
  { value: 'multi_select', label: 'Multi select', hasOptions: true },
  { value: 'checkbox', label: 'Yes / No', hasOptions: false },
];

export const OPTION_FIELD_TYPES = CUSTOM_FIELD_TYPES
  .filter((t) => t.hasOptions)
  .map((t) => t.value);

export const getFieldTypeLabel = (fieldType) =>
  CUSTOM_FIELD_TYPES.find((t) => t.value === fieldType)?.label || fieldType;

/**
 * Column key for a custom field. Namespaced because DataTable has hardcoded
 * rendering branches for keys like `client_company` and `assignees` that ignore
 * customCellRender — a custom field named "Company" must not collide with one.
 */
export const columnKeyFor = (field) => `cf_${field.field_id}`;

export const isCustomColumnKey = (key) => typeof key === 'string' && key.startsWith('cf_');

export const fieldForColumnKey = (fields, key) =>
  fields.find((f) => columnKeyFor(f) === key) || null;

// Raw stored value for a field on a row. Values arrive keyed by field_id.
export const getFieldValue = (row, field) => row?.custom_fields?.[field.field_id] ?? null;

const isEmptyValue = (value) =>
  value === null || value === undefined || value === '' ||
  (Array.isArray(value) && value.length === 0);

/**
 * Whether a field has been left unset, for the detail panel's empty-field
 * collapsing. A checkbox that has been explicitly set to No counts as answered
 * — only one that was never touched is empty.
 */
export const isFieldValueEmpty = (field, value) => {
  if (field.field_type === 'checkbox') return value === null || value === undefined;
  return isEmptyValue(value);
};

const optionFor = (field, value) =>
  (field.options || []).find((o) => String(o.value) === String(value)) || null;

// Maps a definition onto the EditableField `type` that edits it.
export const editorTypeFor = (field) => {
  switch (field.field_type) {
    case 'textarea': return 'textarea';
    case 'number': return 'number';
    case 'currency': return 'number';
    case 'date': return 'date';
    case 'select': return 'select';
    case 'multi_select': return 'multiselect';
    case 'checkbox': return 'checkbox';
    default: return 'text';
  }
};

// Options in the { value, label, color } shape EditableField expects.
export const editorOptionsFor = (field) =>
  OPTION_FIELD_TYPES.includes(field.field_type)
    ? (field.options || []).map((o) => ({ value: String(o.value), label: o.label, color: o.color || null }))
    : [];

const OptionChip = ({ option, value }) => (
  <span
    className={detailStyles.labelTag}
    style={option?.color ? { backgroundColor: option.color } : { backgroundColor: '#6b7280' }}
  >
    {option ? option.label : String(value)}
  </span>
);

/**
 * Display node for a custom field value. `formatMoney` is the page's
 * useCurrency() formatter, so currency fields follow the company currency.
 * Returns null when empty so callers can fall back to their own placeholder.
 */
export const renderCustomValue = (field, value, formatMoney = null) => {
  if (field.field_type === 'checkbox') {
    // A false checkbox is meaningful, not empty — render it as "No".
    if (value === null || value === undefined) return null;
    return value ? 'Yes' : 'No';
  }

  if (isEmptyValue(value)) return null;

  switch (field.field_type) {
    case 'currency':
      return formatMoney
        ? formatMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : String(value);

    case 'number':
      return String(value);

    case 'select':
      return <OptionChip option={optionFor(field, value)} value={value} />;

    case 'multi_select':
      return (
        <div className={detailStyles.labelsList}>
          {(Array.isArray(value) ? value : [value]).map((v) => (
            <OptionChip key={String(v)} option={optionFor(field, v)} value={v} />
          ))}
        </div>
      );

    default:
      return String(value);
  }
};

/**
 * Type-aware comparator. Without this the pages' default comparator lowercases
 * everything, so a Number column would sort 10 before 9.
 */
export const compareCustomValues = (field, a, b) => {
  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  // Empty values sort last in both directions rather than clustering at the top.
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  switch (field.field_type) {
    case 'number':
    case 'currency': {
      return (Number(a) || 0) - (Number(b) || 0);
    }
    case 'date':
      return String(a).localeCompare(String(b));
    case 'checkbox':
      return (a === true ? 1 : 0) - (b === true ? 1 : 0);
    case 'select': {
      const label = (v) => optionFor(field, v)?.label ?? String(v);
      return label(a).toLowerCase().localeCompare(label(b).toLowerCase());
    }
    case 'multi_select': {
      const joined = (v) => (Array.isArray(v) ? v : [v])
        .map((x) => optionFor(field, x)?.label ?? String(x))
        .join(', ')
        .toLowerCase();
      return joined(a).localeCompare(joined(b));
    }
    default:
      return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
  }
};

/**
 * Merges custom field columns into a page's column list.
 *
 * A key the user has already made a choice about keeps that choice; a key they
 * haven't seen before (a newly created field) defaults to visible. That's what
 * lets a new field show up automatically without resetting anyone's other
 * column preferences. Columns for deleted fields drop out.
 */
export const mergeCustomColumns = (baseColumns, fields, savedVisibility = {}) => {
  const applySaved = (col) => (
    Object.prototype.hasOwnProperty.call(savedVisibility, col.key)
      ? { ...col, visible: savedVisibility[col.key] }
      : col
  );

  const builtIn = baseColumns.filter((col) => !isCustomColumnKey(col.key)).map(applySaved);

  const custom = fields.map((field) => applySaved({
    key: columnKeyFor(field),
    label: field.name,
    visible: field.show_in_table !== false,
  }));

  return [...builtIn, ...custom];
};

// Reads a persisted { key: visible } map. Never throws — a corrupt or absent
// entry just means "no preferences yet".
export const loadColumnVisibility = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Persists a { key: visible } map, merging into whatever is already stored.
 *
 * Merging rather than replacing is what makes custom column preferences
 * survive a reload. On mount the page has its built-in columns but has not yet
 * fetched its custom field definitions, so it saves a list with no `cf_*` keys
 * in it. A replacing write erases the user's choices at that moment, and the
 * merge that runs once definitions arrive then reads back the map it just
 * cleared and defaults every custom column to visible again.
 */
export const saveColumnVisibility = (storageKey, columns) => {
  try {
    const map = loadColumnVisibility(storageKey);
    columns.forEach((col) => { map[col.key] = col.visible; });
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    // Storage unavailable (private mode, quota) — preferences just don't persist.
  }
};

/**
 * Drops stored keys for custom fields that no longer exist. Without this a
 * merging write keeps every column key the account has ever had, since a
 * deleted field simply stops appearing in the list being saved.
 */
export const pruneColumnVisibility = (storageKey, fields) => {
  try {
    const stored = loadColumnVisibility(storageKey);
    const live = new Set(fields.map(columnKeyFor));
    const pruned = {};
    Object.keys(stored).forEach((key) => {
      if (!isCustomColumnKey(key) || live.has(key)) pruned[key] = stored[key];
    });
    localStorage.setItem(storageKey, JSON.stringify(pruned));
  } catch {
    // Same as above — a failure here only costs a few stale keys.
  }
};
